/**
 * useConfirm — a Promise-based confirm dialog hook.
 *
 * Usage:
 *
 *   // 1) Wrap the app in <ConfirmProvider> (already mounted in App.js).
 *   //
 *   // 2) In any component:
 *   const confirm = useConfirm();
 *   const onDelete = async () => {
 *     const ok = await confirm({
 *       title: "Delete this lead?",
 *       body: "This cannot be undone.",
 *       confirmLabel: "Delete lead",
 *       confirmTone: "danger",
 *     });
 *     if (ok) doDelete();
 *   };
 *
 * The hook turns the 4-line modal state dance into a single `await`, makes
 * confirm flows trivially testable via the hook's return value, and gives
 * us one central place to A/B-test confirmation copy.
 */
import { createContext, useCallback, useContext, useRef, useState } from "react";
import ConfirmDialog from "@/components/ConfirmDialog";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [config, setConfig] = useState(null);   // { title, body, confirmLabel, confirmTone, testid } | null
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    setConfig(null);
    setBusy(false);
    if (resolverRef.current) {
      const r = resolverRef.current;
      resolverRef.current = null;
      r(result);
    }
  }, []);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setConfig({
        title: opts.title || "Are you sure?",
        body: opts.body || "",
        confirmLabel: opts.confirmLabel || "Continue",
        confirmTone: opts.confirmTone || "primary",
        testid: opts.testid || "use-confirm-dialog",
      });
    });
  }, []);

  // Wrap the confirm action so a slow async `onConfirm` can hold the modal
  // open and disable the buttons via `busy`. The hook itself does not run
  // the destructive action — it just resolves true/false; consumers run it.
  const onConfirm = useCallback(() => {
    setBusy(true);
    // Tiny microtask delay so disabled state paints before resolve fires.
    Promise.resolve().then(() => close(true));
  }, [close]);

  const onCancel = useCallback(() => close(false), [close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {config && (
        <ConfirmDialog
          title={config.title}
          body={config.body}
          confirmLabel={config.confirmLabel}
          confirmTone={config.confirmTone}
          busy={busy}
          onConfirm={onConfirm}
          onCancel={onCancel}
          testid={config.testid}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export default function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback to window.confirm if the provider isn't mounted (e.g. unit
    // tests that render a component in isolation). This keeps the hook
    // usable without a mandatory provider for low-stakes flows.
    return async (opts = {}) => {
      const msg = opts.title ? `${opts.title}\n\n${typeof opts.body === "string" ? opts.body : ""}` : (typeof opts.body === "string" ? opts.body : "Are you sure?");
      // eslint-disable-next-line no-alert
      return Promise.resolve(window.confirm(msg));
    };
  }
  return ctx;
}
